import { type ReactElement, type ReactNode } from 'react';
import { r, s, typographyTokens } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { TableTagChipWithTooltip } from '@/ui/composites/table/cells/TableTagChipWithTooltip';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Text,
  Tooltip,
  getStatusChipColor,
  resolveStatusChipLabel,
  resolveStatusChipTone,
} from '@/ui/primitives';
import { KeyboardArrowDownIcon, MoreVertIcon, SettingsIcon } from '@/ui/primitives/icons';
import { tableV2BodyRowHeightPx, tableV2HeaderHeight } from './TableV2Constants';
import { tableV2HeaderLabelSx } from './TableV2HeaderStyles';
import {
  buildTableV2DenseTagColumns,
  tableV2DenseTagChipSx,
  tableV2DenseTagGroupSx,
  tableV2TagChipSx,
  tableV2TagGroupSx,
} from './TableV2TagGroupStyles';
import type { TagColor } from '@/shared/data/tags';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { SuiteRunStatus, TestRunStatus } from '@flowqa/shared';

export { TableV2SkeletonRow } from './TableV2Skeleton';
export type { TableV2SkeletonColumnKind } from './TableV2Skeleton';
export { TableV2ChevronButton } from './TableV2Chevron';
export { tableV2ChevronCellSx } from './TableV2ChevronCellStyles';
export { TableV2NameCell } from './TableV2NameCell';
export { formatTableV2PassRate } from './TableV2PassRateCell.helpers';
export type { TableV2Counts } from './TableV2PassRateCell.helpers';
export { TableV2PassRateCell, TableV2PassRateStatusBar } from './TableV2PassRateCell';
export { TableV2ProjectCell } from './TableV2ProjectCell';

export {
  tableV2ActionsColumnWidth,
  tableV2BodyRowHeightPx,
  tableV2ChevronColumnWidth,
  tableV2FooterHeight,
  tableV2HeaderHeight,
  tableV2MinimumVisibleRows,
  tableV2NamePrimaryFontSize,
  tableV2NestedBodyRowHeightPx,
  tableV2NestedHeaderHeight,
} from './TableV2Constants';

export const tableV2RootSx = {
  backgroundColor: 'background.paper',
  tableLayout: 'fixed',
  '& th': {
    backgroundColor: 'background.paper',
    borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
    color: 'text.primary',
    fontSize: '16px',
    fontWeight: 400,
    height: tableV2HeaderHeight,
    overflow: 'hidden',
    py: s('none'),
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  '& > tbody > tr > td': {
    boxSizing: 'border-box',
    fontSize: '15px',
    maxHeight: `${String(tableV2BodyRowHeightPx)}px`,
    overflow: 'hidden',
    verticalAlign: 'middle',
  },
  '& .MuiTypography-root': {
    fontSize: '15px',
  },
} satisfies SxProps<Theme>;

export const tableV2PanelSx = {
  backgroundColor: 'background.paper',
  border: (theme) => `1px solid ${theme.app.colors.border}`,
  borderRadius: r('md'),
  overflow: 'hidden',
  overflowX: 'auto',
  width: '100%',
} satisfies SxProps<Theme>;

export const TableV2NestedCollapse = ({
  children,
  expanded,
}: {
  children: ReactNode;
  expanded: boolean;
}): ReactElement | null => {
  return (
    <Collapse
      aria-hidden={!expanded}
      in={expanded}
      timeout={220}
      mountOnEnter
      unmountOnExit
      sx={{
        '& .MuiCollapse-wrapperInner': {
          overflow: 'hidden',
        },
        width: '100%',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'transparent',
          minHeight: 0,
          overflow: 'hidden',
          transform: expanded ? 'translateY(0)' : 'translateY(-8px)',
          transition: (theme) =>
            theme.transitions.create(['opacity', 'transform'], {
              duration: expanded ? 180 : 220,
              easing: theme.transitions.easing.easeInOut,
            }),
          width: '100%',
        }}
      >
        {children}
      </Box>
    </Collapse>
  );
};

export type TableV2PaginationToken = number | 'ellipsis';

const MAX_TABLE_V2_PAGINATION_TOKENS = 7;

export const buildTableV2PaginationTokens = (
  page: number,
  pageCount: number,
): TableV2PaginationToken[] => {
  if (pageCount <= MAX_TABLE_V2_PAGINATION_TOKENS) {
    return Array.from({ length: pageCount }, (_value, index) => index);
  }

  const lastPage = pageCount - 1;
  if (page <= 3) {
    return [0, 1, 2, 3, 4, 'ellipsis', lastPage];
  }

  if (page >= lastPage - 3) {
    return [0, 'ellipsis', lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
  }

  return [0, 'ellipsis', page - 1, page, page + 1, 'ellipsis', lastPage];
};

export const formatTableV2DateTime = (value: Date | string | null | undefined): string => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.valueOf())) return '-';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const tableV2RelativeTimeSx = {
  '&.MuiTypography-root': {
    color: (theme) => theme.app.colors.textMuted,
    fontSize: '14px',
    opacity: 0.72,
  },
} satisfies SxProps<Theme>;

export const TableV2ActionsHeaderIcon = (): ReactElement => (
  <Tooltip title="Actions">
    <Box
      component="span"
      role="img"
      sx={{
        alignItems: 'center',
        display: 'inline-flex',
        height: '100%',
        justifyContent: 'center',
        lineHeight: 0,
        width: '100%',
      }}
    >
      <SettingsIcon
        sx={{
          color: 'text.primary',
          display: 'block',
          fontSize: (theme) => theme.app.iconSizes.md,
          strokeWidth: 0,
        }}
      />
    </Box>
  </Tooltip>
);

export const TableV2RowActionsIconButton = ({ label }: { label: string }): ReactElement => (
  <IconButton
    aria-label={label}
    size="medium"
    sx={{
      borderRadius: r('pill'),
      height: '28px',
      minHeight: '28px',
      minWidth: '28px',
      p: s('none'),
      width: '28px',
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: 'rgba(255, 255, 255, 0.22)',
      },
    }}
  >
    <MoreVertIcon />
  </IconButton>
);

export const TableV2HeaderLabel = ({ label }: { label: string }): ReactElement => (
  <OverflowTooltip
    title={label}
    wrapperSx={{
      maxWidth: '100%',
      minWidth: 0,
      width: '100%',
    }}
  >
    <Box component="span" sx={tableV2HeaderLabelSx}>
      {label}
    </Box>
  </OverflowTooltip>
);

export const TableV2StatusChip = ({
  label,
  status,
}: {
  label?: string;
  status: SuiteRunStatus | TestRunStatus | null | undefined;
}): ReactElement => {
  const statusColor = getStatusChipColor(status);
  const resolvedStatusColor = uiTokens.statusColors[statusColor];
  const resolvedLabel = label ?? resolveStatusChipLabel(status ?? 'untested');

  return (
    <Chip
      size="small"
      tone={resolveStatusChipTone(status)}
      label={resolvedLabel}
      sx={{
        backgroundColor: 'transparent',
        border: `1px solid ${resolvedStatusColor}`,
        borderRadius: r('sm'),
        color: resolvedStatusColor,
        fontWeight: typographyTokens.overline.fontWeight,
        height: '32px',
        '& .MuiChip-label': {
          color: 'inherit',
          px: s('lg'),
          py: s('xs'),
        },
        '& .MuiChip-label .MuiTypography-root': {
          color: resolvedStatusColor,
          fontWeight: typographyTokens.overline.fontWeight,
        },
        '&:hover, &.Mui-focusVisible, &:active': {
          backgroundColor: 'transparent',
        },
      }}
    />
  );
};

export const TableV2DateTimeCell = ({
  value,
}: {
  value: Date | string | null | undefined;
}): ReactElement => {
  const relativeLabel = value ? formatRelativeTime(value) : '';
  const dateTimeLabel = formatTableV2DateTime(value);
  const relativeTimeLabel = relativeLabel.length > 0 ? relativeLabel : '-';

  return (
    <Box sx={{ display: 'grid', gap: s('none'), minWidth: 0 }}>
      <OverflowTooltip title={dateTimeLabel} wrapperSx={{ lineHeight: 1.4 }}>
        <Text
          role="body"
          color="textSecondary"
          component="span"
          sx={{
            display: 'block',
            lineHeight: 1.4,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {dateTimeLabel}
        </Text>
      </OverflowTooltip>
      <OverflowTooltip title={relativeTimeLabel} wrapperSx={{ lineHeight: 1.4 }}>
        <Text
          role="captionSmall"
          component="span"
          sx={[
            tableV2RelativeTimeSx,
            {
              display: 'block',
              lineHeight: 1.4,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          ]}
        >
          {relativeTimeLabel}
        </Text>
      </OverflowTooltip>
    </Box>
  );
};

const tagToneByName: Partial<Record<string, TagColor>> = {
  Backend: 'blue',
  Critical: 'red',
  Customer: 'purple',
  Frontend: 'teal',
  Mobile: 'orange',
  Payments: 'green',
  Security: 'brown',
  Smoke: 'teal',
};

export const TableV2TagGroup = ({
  tagColorByName,
  tags,
}: {
  tagColorByName?: Partial<Record<string, TagColor>>;
  tags: string[];
}): ReactElement => {
  if (tags.length === 0) {
    return (
      <Text role="body" component="span" color="textSecondary">
        -
      </Text>
    );
  }

  const tagGroupSx =
    tags.length > 2
      ? [tableV2DenseTagGroupSx, { gridTemplateColumns: buildTableV2DenseTagColumns(tags.length) }]
      : tableV2TagGroupSx;
  const tagChipSx = tags.length > 2 ? [tableV2TagChipSx, tableV2DenseTagChipSx] : tableV2TagChipSx;

  return (
    <Box sx={tagGroupSx}>
      {tags.map((tag) => {
        const tagTone = tagColorByName?.[tag] ?? tagToneByName[tag] ?? 'neutral';

        return (
          <TableTagChipWithTooltip
            key={tag}
            label={tag}
            sx={tagChipSx}
            tone={tagTone}
            tooltipSx={tableV2TagChipSx}
          />
        );
      })}
    </Box>
  );
};

export const TableV2SortableHeader = <TField extends string>({
  activeSort,
  disabled = false,
  field,
  label,
  onSortChange,
}: {
  activeSort: ApiSort;
  disabled?: boolean;
  field: TField;
  label: string;
  onSortChange: (field: TField) => void;
}): ReactElement => {
  const active = activeSort.field === field;
  const direction = active ? activeSort.direction : 'desc';

  if (disabled) {
    return <TableV2HeaderLabel label={label} />;
  }

  return (
    <Box
      aria-label={
        active
          ? `${label}, sorted ${direction === 'asc' ? 'ascending' : 'descending'}`
          : `${label}, activate to sort descending`
      }
      component="button"
      type="button"
      onClick={() => {
        onSortChange(field);
      }}
      sx={{
        '&:hover .TableV2SortableHeader-icon': {
          opacity: 1,
        },
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        color: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        font: 'inherit',
        gap: s('sm'),
        justifyContent: 'flex-start',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        p: s('none'),
        textAlign: 'left',
      }}
    >
      <OverflowTooltip
        title={label}
        wrapperSx={{
          flex: '0 1 auto',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <Box component="span" sx={tableV2HeaderLabelSx}>
          {label}
        </Box>
      </OverflowTooltip>
      <KeyboardArrowDownIcon
        className="TableV2SortableHeader-icon"
        sx={{
          flexShrink: 0,
          fontSize: (theme) => theme.app.iconSizes.lg,
          opacity: active ? 1 : 0,
          transform: direction === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </Box>
  );
};
